// Section 21 (stream G): the Ledger's eleven sections as tables and charts. Every component
// takes props (the view model); none reads the store. Facts and patterns only, in the text
// table's neutral voice; deaths are named, never highlighted.
import type { JobId, SketchLine } from '../../sim/api';
import type { LedgerVM } from '../../viewModels/ledger';
import type { SeasonStamp } from '../../viewModels/calendar';
import {
  ageGroupPlural,
  arrivalDecisionLabel,
  arrivalReasonLabel,
  causeLabel,
  crimeLabel,
  departureReasonLabel,
  fateLabel,
  fillSketch,
  improvementLabel,
  justiceResponseLabel,
  ledgerEventLabel,
  ledgerNormLabel,
  ledgerOptionsLine,
  ledgerText as t,
  originLabel,
  rationResourceLabel,
  seasonLabel,
  skillLabelForLedger,
} from '../text/en';
import { LineChart } from './line-chart';

export const when = (s: SeasonStamp): string => t.when(seasonLabel[s.season], s.year);
const whenTurn = (turn: number, perYear: number): string =>
  t.when(
    seasonLabel[
      (['spring', 'summer', 'autumn', 'winter'] as const)[(turn - 1) % perYear] ?? 'spring'
    ],
    Math.floor((turn - 1) / perYear) + 1,
  );
const fixed = (x: number, digits = 1): string => x.toFixed(digits);

function Empty() {
  return <p class="hint">{t.none}</p>;
}

function Table({ head, rows }: { head: string[]; rows: (string | number)[][] }) {
  if (rows.length === 0) return <Empty />;
  return (
    <table class="ledger__table">
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h} scope="col">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => (
              <td key={j} class={typeof c === 'number' ? 'num' : undefined}>
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Facts({ items }: { items: [string, string | number][] }) {
  return (
    <dl class="ledger__facts">
      {items.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd class="num">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function OutcomeSection({ vm }: { vm: LedgerVM }) {
  const o = vm.outcome;
  return (
    <div>
      <Facts
        items={[
          [t.seasons, o.seasons],
          [t.seed, o.seed],
          [t.ironman, o.ironman ? t.yes : t.no],
          [t.sandbox, o.sandbox ? t.yes : t.no],
        ]}
      />
      <p>{t.steadyCount(o.counter, o.target)}</p>
      <p>
        {t.options}: {ledgerOptionsLine(o.options)}
      </p>
    </div>
  );
}

export function PeopleSection({ vm }: { vm: LedgerVM }) {
  const p = vm.people;
  const yearsOr = (y: number | null): string => (y === null ? '' : fixed(y));
  return (
    <div>
      <Facts
        items={[
          [t.births, p.births],
          [t.deaths, p.deaths],
          [t.peak, p.peak],
          [t.final, p.final],
          [t.oldest, p.oldest === null ? '' : `${p.oldest.name}, ${p.oldest.years}`],
          [
            t.youngestDeath,
            p.youngestDeath === null
              ? ''
              : `${p.youngestDeath.name}, ${p.youngestDeath.years} (${causeLabel[p.youngestDeath.cause]}, ${when(p.youngestDeath.at)})`,
          ],
        ]}
      />
      <h4>{t.deathsByCause}</h4>
      <Table
        head={[t.deaths, '']}
        rows={p.deathsByCause.map((d) => [causeLabel[d.cause], d.count])}
      />
      <Table
        head={[t.origin, t.lifeExpectancy, t.meanAgeAtDeath]}
        rows={p.lifeExpectancyYears.map((r) => [
          originLabel[r.origin],
          yearsOr(r.years),
          yearsOr(r.atDeathYears),
        ])}
      />
    </div>
  );
}

/** Section 21: "Children were fed short in 3% of person-seasons; elders in 22%." */
export function shortSummary(vm: LedgerVM): string {
  const parts = vm.wentWithout.groups
    .filter((g) => g.personSeasons > 0)
    .map((g, i) => t.shortPart(ageGroupPlural[g.group], g.percent, i === 0));
  return parts.length === 0 ? '' : t.shortLine(parts);
}

export function WentWithoutSection({ vm }: { vm: LedgerVM }) {
  const summary = shortSummary(vm);
  return (
    <div>
      {summary !== '' && <p>{summary}</p>}
      <Table
        head={[t.name, t.group, t.origin, t.seasonsShort, t.seasonsNone, t.fate]}
        rows={vm.wentWithout.rows.map((r) => [
          r.name,
          ageGroupPlural[r.group],
          originLabel[r.origin],
          r.seasonsShort,
          r.seasonsNone,
          fateLabel[r.fate],
        ])}
      />
    </div>
  );
}

export function ChosenSection({ vm }: { vm: LedgerVM }) {
  return (
    <Table
      head={[t.season, t.resource, t.shortfall, t.pinnedNone, t.byHand]}
      rows={vm.chosen.map((c) => [
        when(c.at),
        rationResourceLabel[c.resource],
        fixed(c.shortfall),
        c.pinnedNone.join(', '),
        c.byHand ? t.yes : t.no,
      ])}
    />
  );
}

export function NormsSection({ vm }: { vm: LedgerVM }) {
  return (
    <div>
      <Table
        head={[t.season, t.norm, t.change, t.announced]}
        rows={vm.norms.rows.map((n) => [
          when(n.at),
          ledgerNormLabel[n.normId],
          t.normChange[n.change],
          n.announced ? t.yes : t.no,
        ])}
      />
      <h4>{t.policyChanges}</h4>
      <Table
        head={[t.season, t.policy, t.value]}
        rows={vm.norms.policies.map((p) => [when(p.at), p.label, p.value])}
      />
    </div>
  );
}

export function JusticeSection({ vm }: { vm: LedgerVM }) {
  const j = vm.justice;
  return (
    <div>
      <Facts
        items={[
          [justiceResponseLabel.flogging, j.corporalPunishments],
          [justiceResponseLabel.execution, j.executions],
          [justiceResponseLabel.exile, j.exiles],
        ]}
      />
      <Table
        head={[t.season, t.crime, t.offender, t.response, t.reoffended, t.fate]}
        rows={j.rows.map((c) => [
          when(c.at),
          crimeLabel[c.crime],
          c.offender,
          c.response === null ? t.pending : justiceResponseLabel[c.response],
          c.reoffended ? t.yes : t.no,
          c.afterSeasons === null
            ? fateLabel[c.fate]
            : `${fateLabel[c.fate]} ${t.after(c.afterSeasons)}`,
        ])}
      />
    </div>
  );
}

export function GateSection({ vm }: { vm: LedgerVM }) {
  const g = vm.gate;
  return (
    <div>
      <Facts
        items={[
          [t.admittedShare, `${g.admittedPercent}%`],
          [t.turnedAwayShare, `${g.turnedAwayPercent}%`],
        ]}
      />
      <Table
        head={[t.season, t.size, t.reason, t.decision, t.admitted]}
        rows={g.rows.map((a) => [
          when(a.at),
          a.size,
          arrivalReasonLabel[a.reason],
          arrivalDecisionLabel[a.decision],
          a.admitted,
        ])}
      />
      <h4>{t.groupFates}</h4>
      <Table
        head={[
          t.origin,
          t.people,
          t.alive,
          t.died,
          t.left,
          t.lifeExpectancy,
          t.meanShort,
          t.trustAtEnd,
        ]}
        rows={g.groups.map((x) => [
          originLabel[x.origin],
          x.fate.people,
          x.fate.alive,
          x.fate.died,
          x.fate.left,
          x.lifeExpectancyYears === null ? '' : fixed(x.lifeExpectancyYears),
          fixed(x.fate.meanSeasonsShort),
          x.fate.trustAtEnd === null ? '' : Math.round(x.fate.trustAtEnd),
        ])}
      />
      <h4>{t.departures}</h4>
      <Table
        head={[t.season, t.people, t.reason]}
        rows={g.departures.map((d) => [
          when(d.at),
          d.names.join(', '),
          departureReasonLabel[d.reason as keyof typeof departureReasonLabel] ?? d.reason,
        ])}
      />
    </div>
  );
}

export function InequalitySection({ vm, perYear }: { vm: LedgerVM; perYear: number }) {
  const i = vm.inequality;
  return (
    <div>
      <p>
        {i.peakAt === null ? '' : `${t.giniPeak(fixed(i.peak, 2), i.peakAt.year)}. `}
        {t.giniMean(fixed(i.mean, 3))}
      </p>
      <LineChart
        id="ledger-gini"
        title={t.giniChart}
        points={i.series}
        formatY={(y) => fixed(y, 2)}
        formatX={(x) => whenTurn(x, perYear)}
        seasonsPerYear={perYear}
      />
    </div>
  );
}

export function KnowledgeSection({
  vm,
  perYear,
  jobLabel,
}: {
  vm: LedgerVM;
  perYear: number;
  jobLabel: (job: JobId) => string;
}) {
  const k = vm.knowledge;
  return (
    <div>
      <p>{t.finalLiteracy(k.finalLiteracyPercent)}</p>
      <LineChart
        id="ledger-literacy"
        title={t.literacyChart}
        points={k.literacy}
        yMax={100}
        formatY={(y) => `${Math.round(y)}%`}
        formatX={(x) => whenTurn(x, perYear)}
        seasonsPerYear={perYear}
      />
      <h4>{t.skillsLost}</h4>
      <Table
        head={[t.season, t.skill, t.name, t.level]}
        rows={k.skillsLost.map((s) => [
          when(s.at),
          skillLabelForLedger(s.skill, jobLabel),
          s.name,
          fixed(s.level),
        ])}
      />
      <h4>{t.improvements}</h4>
      <Table
        head={[t.season, t.improvement]}
        rows={k.improvements.map((x) => [when(x.at), improvementLabel[x.improvementId]])}
      />
    </div>
  );
}

export function FragilitySection({ vm, perYear }: { vm: LedgerVM; perYear: number }) {
  const f = vm.fragility;
  const marks = f.marks.map((m) => ({
    x: m.x,
    label:
      m.eventIds.length > 0
        ? m.eventIds.map((id) => ledgerEventLabel[id]).join(', ')
        : t.events(m.count),
  }));
  return (
    <div>
      {f.peakAt !== null && <p>{t.fragilityPeak(Math.round(f.peak), f.peakAt.year)}</p>}
      <LineChart
        id="ledger-fragility"
        title={t.fragilityChart}
        points={f.series}
        yMax={100}
        formatY={(y) => String(Math.round(y))}
        formatX={(x) => whenTurn(x, perYear)}
        marks={marks}
        seasonsPerYear={perYear}
      />
    </div>
  );
}

export function SketchSection({ sketch }: { sketch: readonly SketchLine[] }) {
  return <p class="ledger__sketch">{sketch.map(fillSketch).join(' ')}</p>;
}
